/**
 * AGENT-BC: combo-chart data hook for the PBI-faithful dashboard.
 *
 * Replaces the legacy hook that consumed `{table, charts: {revenueQuoteFunnel,
 * revenueOnlineOrders}}`. The new backend (`POST /api/dashboard/report`,
 * Agent BA) returns a `{pivot, leftChart, rightChart}` shape — both combo
 * charts read from `reportData.leftChart` / `reportData.rightChart` and the
 * series count/types are whatever PBI's underlying visual emits.
 *
 * Wire contract (canonical types live in `../types`, owned by Agent BB):
 *
 *   ComboChartPayload {
 *     visualId:   string;
 *     title:      string;
 *     xAxis:      string[];
 *     series:     Array<{name, type:"bar"|"line", data: number[]}>;
 *     filterNote: string | null;
 *   }
 *
 *   ReportResponse {
 *     pivot:      PivotPayload;
 *     leftChart:  ComboChartPayload;
 *     rightChart: ComboChartPayload;
 *   }
 */

import { useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";
import { postReport } from "../api/client";
import { useFilterStore } from "../state/filterStore";
import type {
  ComboChartPayload,
  ComboSeries,
  ReportResponse,
} from "../types";

/** AGENT-BC: re-export the canonical types so the chart components only need
 *  to import from `./useReportData`. */
export type { ComboChartPayload, ComboSeries, ReportResponse };
/** Back-compat alias preserved for any consumer that imported `ReportData`. */
export type ReportData = ReportResponse;

/* ------------------------------------------------------------------------- *
 * AGENT-BC: the hook.
 * ------------------------------------------------------------------------- */

/**
 * `useReportData()` — single hook for both combo charts (and BB's metrics
 * table — react-query dedupes on the key, so all three consumers share one
 * fetch).
 *
 * - Pulls the filter state from `useFilterStore`.
 * - Calls `postReport(state.toServerShape())`.
 * - Query key: `["dashboard-report", JSON.stringify(serverShape)]` so any
 *   filter change triggers a refetch.
 * - `staleTime: 10_000`, `placeholderData: keepPrevious` so filter flips don't
 *   blank the charts mid-transition.
 */
export function useReportData(): UseQueryResult<ReportResponse> {
  // AGENT-PV8: see MetricsTable.tsx for the rationale — subscribe to the
  // filter-state slice (not just the function reference) so the chart
  // hook re-renders and the queryKey changes on any filter mutation.
  const filterState = useFilterStore(
    useShallow((s) => ({
      categories: s.categories,
      parentProducts: s.parentProducts,
      partNumbers: s.partNumbers,
      vendors: s.vendors,
      booleans: s.booleans,
      // `online` is the tri-state Online Order toggle. It lives outside
      // `booleans` (so the other 7 boolean toggles stay typed as plain bool),
      // and `toServerShape()` projects it into wire `booleans.online`. The
      // useShallow slice MUST include it — otherwise toggling Online Order
      // doesn't change the slice ref, useMemo doesn't recompute the wire
      // shape, the query key stays identical, and react-query never refetches.
      online: s.online,
      newOrExisting: s.newOrExisting,
      mode: s.mode,
      dateRange: s.dateRange,
      viewLeft: s.viewLeft,
      viewRight: s.viewRight,
      pivotVisualId: s.pivotVisualId,
    })),
  );
  const toServerShape = useFilterStore((s) => s.toServerShape);
  const filterShape = useMemo(
    () => toServerShape(),
    [filterState, toServerShape],
  );
  // useMemo over the JSON so the query key only changes when the shape does.
  const filterKey = useMemo(() => JSON.stringify(filterShape), [filterShape]);
  return useQuery<ReportResponse>({
    queryKey: ["dashboard-report", filterKey],
    queryFn: ({ signal }) => postReport(filterShape, signal),
    staleTime: 10_000,
    placeholderData: (prev) => prev, // react-query v5 equivalent of keepPreviousData
  });
}

/* ------------------------------------------------------------------------- *
 * AGENT-BC: x-axis null defense (preserved from prior agent — keep it).
 *
 * ECharts will happily render a `null` category as the literal string "null"
 * or leave a phantom gap. We coerce everything to a string and substitute
 * "—" for any nullish entry, logging a console warning so upstream contract
 * violations surface during development.
 *
 * Returns a guaranteed string[]; if the input is empty we return [] and the
 * caller is expected to render the empty-state placeholder.
 * ------------------------------------------------------------------------- */

export function sanitizeXAxis(
  raw: unknown,
  chartId: string,
): string[] {
  if (!Array.isArray(raw)) {
    if (raw !== undefined && raw !== null) {
      // eslint-disable-next-line no-console
      console.warn(
        `[charts/${chartId}] xAxis was not an array (got ${typeof raw}); rendering empty`,
      );
    }
    return [];
  }
  if (raw.length === 0) return [];

  let hadNull = false;
  let coerced = false;
  const out: string[] = raw.map((entry) => {
    if (entry === null || entry === undefined) {
      hadNull = true;
      return "—"; // em-dash
    }
    if (typeof entry === "string") return entry;
    coerced = true;
    return String(entry);
  });

  if (hadNull) {
    // eslint-disable-next-line no-console
    console.warn(
      `[charts/${chartId}] xAxis contained null entries; replaced with em-dash`,
    );
  }
  if (coerced) {
    // eslint-disable-next-line no-console
    console.warn(
      `[charts/${chartId}] xAxis contained non-string entries; coerced via String()`,
    );
  }
  return out;
}
